// Client-side rollups, ported from the embedded JS renderer in
// cleo-organic-tracker/report.py. Avg position stays impression-weighted on
// every rollup: Σ(pos × impr) / Σimpr.

import { GroupData, MetricKey, PageRow, PmtdPage, PmtdQuery, QueryRow, SeoReport } from './seoTypes';

/** Value of a page metric for a given month index (CTR derived inline). */
export function pageMetric(p: PageRow, key: MetricKey, i: number): number {
    if (key === 'ctr') return p.impr[i] ? p.clicks[i] / p.impr[i] : 0;
    if (key === 'position') return p.pos[i];
    return (p as unknown as Record<string, number[]>)[key][i];
}

// ---- Same-period (prior-month-to-date) MoM baselines ----
// For the partial current month, the comparison baseline is the prior month
// clipped to the same day count (pmtd) instead of the full prior month, so a
// month-to-date figure isn't measured against a complete month. Completed months
// keep the normal full-vs-full prior-month comparison.

/** True when the selected month is the partial (month-to-date) month. */
export function isMtdMonth(report: SeoReport, sel: number): boolean {
    return report.mtdMonth != null && report.months[sel] === report.mtdMonth;
}

/** A page metric value from prior-month-to-date scalars. */
export function pmtdPageMetric(pm: PmtdPage, key: MetricKey): number {
    if (key === 'ctr') return pm.impr ? pm.clicks / pm.impr : 0;
    if (key === 'position') return pm.pos;
    return (pm as unknown as Record<string, number>)[key];
}

/**
 * MoM comparison baseline for a page metric in the selected month: the same-
 * period prior month (pmtd) for the partial current month when available, else
 * the full prior month. Returns null when there's no prior data.
 */
export function pagePrevMetric(report: SeoReport, p: PageRow, key: MetricKey, sel: number): number | null {
    if (isMtdMonth(report, sel) && p.pmtd) return pmtdPageMetric(p.pmtd, key);
    return sel - 1 >= 0 ? pageMetric(p, key, sel - 1) : null;
}

/** A query metric value (impr/clicks/ctr/position) from pmtd scalars. */
export function pmtdQueryMetric(pm: PmtdQuery, key: 'impr' | 'clicks' | 'ctr' | 'position'): number {
    if (key === 'ctr') return pm.impr ? pm.clicks / pm.impr : 0;
    if (key === 'position') return pm.pos;
    return pm[key];
}

/** MoM baseline for a query's impressions (pmtd-aware). */
export function queryPrevImpr(report: SeoReport, r: QueryRow, sel: number): number | null {
    if (isMtdMonth(report, sel) && r.pmtd) return r.pmtd.impr;
    return sel - 1 >= 0 ? r.impr[sel - 1] : null;
}

/** Total impressions across the whole window — used to fold zero-data pages. */
export function windowTotal(p: PageRow): number {
    return p.impr.reduce((a, b) => a + b, 0);
}

export interface GroupMonthSum {
    impr: number;
    clicks: number;
    fills: number;
    starts: number;
    phone: number;
    sessions: number;
    ctr: number;
    position: number;
}

/** Sum a group's pages for one month index (impression-weighted position). */
export function groupSum(g: GroupData, i: number): GroupMonthSum {
    let impr = 0;
    let clicks = 0;
    let fills = 0;
    let starts = 0;
    let phone = 0;
    let sessions = 0;
    let posw = 0;
    for (const p of g.pages) {
        impr += p.impr[i];
        clicks += p.clicks[i];
        fills += p.fills[i];
        starts += p.starts[i];
        phone += p.phone[i];
        sessions += p.sessions[i];
        posw += p.pos[i] * p.impr[i];
    }
    return { impr, clicks, fills, starts, phone, sessions, ctr: impr ? clicks / impr : 0, position: impr ? posw / impr : 0 };
}

export function groupSumValue(s: GroupMonthSum, key: MetricKey): number {
    if (key === 'ctr') return s.ctr;
    if (key === 'position') return s.position;
    return s[key as 'impr' | 'clicks' | 'fills' | 'starts' | 'phone' | 'sessions'];
}

/**
 * Previous-period group rollup (pmtd-aware): for the partial current month it
 * sums each page's prior-month-to-date scalars; otherwise the full prior month.
 * Returns null when there's no prior data to compare against.
 */
export function groupPrevSum(report: SeoReport, g: GroupData, sel: number): GroupMonthSum | null {
    if (isMtdMonth(report, sel)) {
        let any = false;
        let impr = 0, clicks = 0, fills = 0, starts = 0, phone = 0, sessions = 0, posw = 0;
        for (const p of g.pages) {
            if (!p.pmtd) continue;
            any = true;
            impr += p.pmtd.impr;
            clicks += p.pmtd.clicks;
            fills += p.pmtd.fills;
            starts += p.pmtd.starts;
            phone += p.pmtd.phone;
            sessions += p.pmtd.sessions;
            posw += p.pmtd.pos * p.pmtd.impr;
        }
        if (any) {
            return { impr, clicks, fills, starts, phone, sessions, ctr: impr ? clicks / impr : 0, position: impr ? posw / impr : 0 };
        }
    }
    return sel - 1 >= 0 ? groupSum(g, sel - 1) : null;
}

/** Full-window impressions series for a group (for the trend sparkline). */
export function groupSeries(g: GroupData, n: number): number[] {
    const a: number[] = [];
    for (let k = 0; k < n; k++) {
        let t = 0;
        for (const p of g.pages) t += p.impr[k];
        a.push(t);
    }
    return a;
}

/** A subtotal "page" summing the given pages across all months (incl. pmtd). */
export function sumPages(pages: PageRow[], n: number): PageRow {
    const impr = new Array(n).fill(0);
    const clicks = new Array(n).fill(0);
    const fills = new Array(n).fill(0);
    const starts = new Array(n).fill(0);
    const phone = new Array(n).fill(0);
    const sessions = new Array(n).fill(0);
    const posw = new Array(n).fill(0);
    // Prior-month-to-date subtotal — accumulated only from pages that carry it.
    let anyPmtd = false;
    const pm = { impr: 0, clicks: 0, fills: 0, starts: 0, phone: 0, sessions: 0, posw: 0 };
    for (const p of pages) {
        for (let k = 0; k < n; k++) {
            impr[k] += p.impr[k];
            clicks[k] += p.clicks[k];
            fills[k] += p.fills[k];
            starts[k] += p.starts[k];
            phone[k] += p.phone[k];
            sessions[k] += p.sessions[k];
            posw[k] += p.pos[k] * p.impr[k];
        }
        if (p.pmtd) {
            anyPmtd = true;
            pm.impr += p.pmtd.impr;
            pm.clicks += p.pmtd.clicks;
            pm.fills += p.pmtd.fills;
            pm.starts += p.pmtd.starts;
            pm.phone += p.pmtd.phone;
            pm.sessions += p.pmtd.sessions;
            pm.posw += p.pmtd.pos * p.pmtd.impr;
        }
    }
    const pos = impr.map((im, k) => (im ? posw[k] / im : 0));
    const row: PageRow = { label: 'Subtotal', kind: 'parent', impr, clicks, fills, starts, phone, pos, sessions, lastmod: null };
    if (anyPmtd) {
        row.pmtd = {
            impr: pm.impr, clicks: pm.clicks, fills: pm.fills, starts: pm.starts, phone: pm.phone,
            pos: pm.impr ? pm.posw / pm.impr : 0, sessions: pm.sessions,
        };
    }
    return row;
}
